/**
 * BeginAdventureOverlay - one-time post-creation hero moment
 *
 * Full-screen overlay shown ONCE right after character creation completes
 * (`heroOverlayCharId` set by the creation stream-end handler): the new
 * hero's portrait, their name, and a single "Begin Your Adventure" button
 * that fires the server-side quick-start for Goldvale
 * (`DEFAULT_QUICK_START_MODULE_ID`), named explicitly because the copy here
 * names it too. An absent id would mean "keep whatever the party already
 * pinned", which sends a player promised Goldvale into the Bleeding Gate.
 * "Look around first" dismisses to the lobby, where the persistent
 * AdventureCtaPanel keeps the module-less quick-start that does respect an
 * existing pin. Players who want the other adventure get there through
 * Tavian, who recommends by party size.
 *
 * Reads character state (not chat state), so it survives the RedirectToNpc
 * that follows creation. Not persisted: a reload lands on the normal lobby.
 * z-40 keeps it under the game-transition ReconnectScreen.
 */

import { useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Swords } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { websocketService } from '@/lib/websocket-service';
import { DEFAULT_QUICK_START_MODULE_ID } from '@danda/shared';

export default function BeginAdventureOverlay() {
  const heroOverlayCharId = useLobbyStore((state) => state.heroOverlayCharId);
  const characters = useLobbyStore((state) => state.characters);
  const quickStartPending = useLobbyStore((state) => state.quickStartPending);
  const pendingGameStart = useLobbyStore((state) => state.pendingGameStart);
  const setQuickStartPending = useLobbyStore((state) => state.setQuickStartPending);
  const setHeroOverlayCharId = useLobbyStore((state) => state.setHeroOverlayCharId);

  const partySelectedModuleId = useLobbyStore((state) => {
    if (!state.currentPartyId) return undefined;
    return state.parties.find((p) => p.id === state.currentPartyId)?.selectedModuleId;
  });

  // What the party had selected when this hero's overlay opened, and whether
  // the player has since started from here. Only a CHANGE away from the
  // opening value counts as "chose an adventure elsewhere": a party that
  // already had one selected must still get the full reveal, and our own
  // quick-start pins a module server-side before it can fail, so treating
  // that as an outside choice would yank the CTA away exactly when the
  // player needs it to retry. Refs, not state: neither drives a render.
  const openedForCharRef = useRef<string | null>(null);
  const moduleAtOpenRef = useRef<string | undefined>(undefined);
  const startedFromOverlayRef = useRef(false);
  if (heroOverlayCharId && openedForCharRef.current !== heroOverlayCharId) {
    openedForCharRef.current = heroOverlayCharId;
    moduleAtOpenRef.current = partySelectedModuleId;
    startedFromOverlayRef.current = false;
  }

  const character = heroOverlayCharId
    ? (characters.find((c) => c.id === heroOverlayCharId) ?? null)
    : null;

  const handleBegin = useCallback(() => {
    // Named explicitly rather than left to the server default. `quick_start.rs`
    // treats an absent id as "matches whatever the party already selected", so
    // a party carrying a Bleeding Gate pin would launch into the Gate under
    // copy that just promised Goldvale. Passing the id re-pins to what we said.
    startedFromOverlayRef.current = true;
    setQuickStartPending(true);
    websocketService.quickStartAdventure(DEFAULT_QUICK_START_MODULE_ID);
  }, [setQuickStartPending]);

  const handleDismiss = useCallback(() => {
    setHeroOverlayCharId(null);
  }, [setHeroOverlayCharId]);

  // A module selected through party chat (Tavian) must also close this
  // overlay: it is a fixed z-40 click-catcher whose only other exits are its
  // own buttons, so without this a chat-side selection left it invisibly
  // swallowing every click on the lobby underneath. A start launched from
  // here is not that: it exits via pendingGameStart when it succeeds, and
  // must leave the CTA up when it fails so the player can retry.
  useEffect(() => {
    if (!heroOverlayCharId || quickStartPending || startedFromOverlayRef.current) return;
    if (partySelectedModuleId && partySelectedModuleId !== moduleAtOpenRef.current) {
      setHeroOverlayCharId(null);
    }
  }, [heroOverlayCharId, partySelectedModuleId, quickStartPending, setHeroOverlayCharId]);

  // The portal transition (ReconnectScreen) renders in normal flow without a
  // z-index; step aside the moment a game start is in flight or this fixed
  // z-40 overlay would sit on top of it.
  if (!character || pendingGameStart) {
    return null;
  }

  const characterClass = character.permanent?.class;
  const species = character.permanent?.species;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-background/95 backdrop-blur-sm overflow-y-auto py-8"
      data-testid="begin-adventure-overlay"
    >
      <div className="flex flex-col items-center gap-6 px-6 text-center max-w-2xl">
        {character.portraitUrl ? (
          // Full-body profile: the authored 9:16 hero portrait.
          <img
            src={character.portraitUrl}
            alt={character.name}
            className="w-48 h-64 object-cover rounded-lg border-2 border-accent-gold/60 shadow-[0_0_40px_-8px] shadow-accent-gold/40"
          />
        ) : character.avatarUrl ? (
          // No profile: fall back to the round avatar, which must render as a
          // circle, not stretched into the portrait rectangle.
          <img
            src={character.avatarUrl}
            alt={character.name}
            className="w-40 h-40 object-cover rounded-full border-2 border-accent-gold/60 shadow-[0_0_40px_-8px] shadow-accent-gold/40"
          />
        ) : (
          <div className="w-48 h-64 flex items-center justify-center rounded-lg border-2 border-accent-gold/60 bg-accent-gold/5 text-8xl">
            {character.avatarEmoji ?? '🛡️'}
          </div>
        )}

        <div className="space-y-1">
          <h2 className="text-2xl font-semibold text-foreground">{character.name}</h2>
          {(species || characterClass) && (
            <p className="text-sm text-muted-foreground">
              {[species, characterClass].filter(Boolean).join(' ')}
            </p>
          )}
          <p className="text-lg font-bold text-foreground pt-2">
            Your hero stands ready. The village of Goldvale needs you.
          </p>
          <div className="space-y-1 pt-1 text-base leading-relaxed text-foreground/80">
            <p>Goldvale is built for 1-3 players and best played with 3.</p>
            <p>
              Go alone if you like, or bring your friends to your party by inviting them from the
              camp.
            </p>
          </div>
        </div>

        <Button
          id="begin-adventure-overlay-cta"
          size="lg"
          className="w-full bg-accent-gold text-background font-semibold text-base hover:bg-accent-gold/90"
          disabled={quickStartPending}
          onClick={handleBegin}
        >
          {quickStartPending ? (
            <span className="flex items-center gap-2">
              <AnkhSpinner size="sm" className="text-background" />
              Opening the portal...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Swords className="h-5 w-5" />
              Begin Your Adventure
            </span>
          )}
        </Button>

        <Button type="button" variant="outline" onClick={handleDismiss}>
          Look around first
        </Button>
      </div>
    </div>
  );
}
